import type React from "react";
import type { Dispatch, RefObject, SetStateAction } from "react";
import { useCallback } from "react";
import { barTapAtPoint, PLOT_HEIGHT } from "./DensityChart.lib";
import type { DensityBarSegment } from "./densityBars.lib";
import { segmentAtPoint } from "./densityBars.lib";
import type { DensityChartBucket } from "./densityBuckets.lib";
import { isWindowHandle } from "./windowOverlay.lib";
import type { HoveredSlice } from "./useSliceHover";
import { isSameSlice } from "./useSliceHover";

interface UseBarHoverParams {
    plotAreaRef: RefObject<HTMLDivElement | null>;
    buckets: DensityChartBucket[];
    zoneCount: number;
    segments: DensityBarSegment[];
    /** The starts of the buckets with a segment. */
    busyBucketStarts: ReadonlySet<string>;
    isLoading: boolean;
    hoverSlice: (slice: HoveredSlice | null) => void;
    /** Sets the hovered bar, which the chart also clears when the bars move. */
    setHoveredBar: Dispatch<SetStateAction<HoveredSlice | null>>;
}

/**
 * What the pointer hovers over the bars: a segment, a bar's track off its
 * segments, or nothing in the zone before the start, on a window handle or
 * while loading. The hovered bar follows the pointer at once, for the hover
 * fills; the tooltip opens through `hoverSlice` for a segment or an empty
 * column only.
 */
export function useBarHover({
    plotAreaRef,
    buckets,
    zoneCount,
    segments,
    busyBucketStarts,
    isLoading,
    hoverSlice,
    setHoveredBar,
}: UseBarHoverParams) {
    const showHoveredBar = (next: HoveredSlice | null) =>
        setHoveredBar((current) =>
            isSameSlice(current, next) ? current : next,
        );
    /** The series of the segment at `y` px in the bucket's bar, if any. */
    const seriesAt = useCallback(
        ({ bucket, y }: { bucket: DensityChartBucket; y: number }) =>
            segmentAtPoint({
                segments: segments.filter(
                    (segment) => segment.bucket === bucket.start,
                ),
                plotHeight: PLOT_HEIGHT,
                y,
            }),
        [segments],
    );
    const handlePointerMove = (event: React.PointerEvent<HTMLDivElement>) => {
        const rect = plotAreaRef.current?.getBoundingClientRect();
        if (!rect) {
            return;
        }
        const { barIndex, y } = barTapAtPoint({
            rect,
            point: event,
            barCount: buckets.length,
        });
        const index = y >= 0 && y <= rect.height ? barIndex : null;
        const bucket = index === null ? undefined : buckets[index];
        if (
            isLoading ||
            isWindowHandle(event.target) ||
            index === null ||
            !bucket ||
            index < zoneCount
        ) {
            showHoveredBar(null);
            hoverSlice(null);
            return;
        }
        const isBusy = busyBucketStarts.has(bucket.start);
        const seriesId = isBusy ? seriesAt({ bucket, y }) : null;
        const hovered = { bucketIndex: index, seriesId };
        showHoveredBar(hovered);
        hoverSlice(isBusy && seriesId === null ? null : hovered);
    };
    const handlePointerLeave = () => showHoveredBar(null);

    return { seriesAt, handlePointerMove, handlePointerLeave };
}
